import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteClient } from '@/hooks/useClients'

interface DeleteClientDialogProps {
  /** Whether dialog is open */
  open: boolean
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** ID of client to delete */
  clientId: string
  /** Name of client (for display in confirmation message) */
  clientName: string
}

/**
 * Confirmation dialog for deleting a client
 * Performs soft delete (sets deleted_at timestamp)
 */
export function DeleteClientDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
}: DeleteClientDialogProps) {
  const deleteMutation = useDeleteClient()

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(clientId)
      onOpenChange(false)
    } catch {
      // Error handling is done in the mutation hook
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Client</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>"{clientName}"</strong>?
            <br />
            <br />
            This will also remove all associated contacts and records.
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Client'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
